// DEFCON TERMINAL - Procedural Sound Generator
// Generates game sounds using Web Audio API without external files

/**
 * Parameters for generating oscillator-based sounds
 */
export interface OscillatorParams {
  type: OscillatorType;
  frequency: number;
  duration: number;
  gain?: number;
  fadeIn?: number;
  fadeOut?: number;
}

/**
 * Parameters for noise-based sounds
 */
export interface NoiseParams {
  duration: number;
  gain?: number;
  filterFrequency?: number;
  filterType?: BiquadFilterType;
}

/**
 * Envelope parameters for amplitude shaping
 */
export interface EnvelopeParams {
  attack: number;
  decay: number;
  sustain: number;
  release: number;
}

/**
 * SoundGenerator - Creates procedural audio using Web Audio API
 * All sounds are generated mathematically without external files
 */
export class SoundGenerator {
  private context: AudioContext;
  private masterGain: GainNode;

  constructor(context: AudioContext) {
    this.context = context;
    this.masterGain = context.createGain();
    this.masterGain.connect(context.destination);
  }

  /**
   * Set master volume for all generated sounds
   */
  setMasterVolume(volume: number): void {
    this.masterGain.gain.setValueAtTime(
      Math.max(0, Math.min(1, volume)),
      this.context.currentTime
    );
  }

  /**
   * Generate a simple tone with optional envelope
   */
  generateTone(params: OscillatorParams): AudioBufferSourceNode {
    const { type, frequency, duration, gain = 0.3, fadeIn = 0.01, fadeOut = 0.01 } = params;

    // Create buffer for the tone
    const sampleRate = this.context.sampleRate;
    const bufferLength = Math.ceil(sampleRate * duration);
    const buffer = this.context.createBuffer(1, bufferLength, sampleRate);
    const data = buffer.getChannelData(0);

    // Generate waveform
    for (let i = 0; i < bufferLength; i++) {
      const t = i / sampleRate;
      let sample = 0;

      switch (type) {
        case 'sine':
          sample = Math.sin(2 * Math.PI * frequency * t);
          break;
        case 'square':
          sample = Math.sin(2 * Math.PI * frequency * t) > 0 ? 1 : -1;
          break;
        case 'sawtooth':
          sample = 2 * ((frequency * t) % 1) - 1;
          break;
        case 'triangle':
          sample = Math.abs(4 * ((frequency * t) % 1) - 2) - 1;
          break;
        default:
          sample = Math.sin(2 * Math.PI * frequency * t);
      }

      // Apply envelope
      const fadeInSamples = Math.ceil(fadeIn * sampleRate);
      const fadeOutSamples = Math.ceil(fadeOut * sampleRate);
      let envelope = 1;

      if (i < fadeInSamples) {
        envelope = i / fadeInSamples;
      } else if (i > bufferLength - fadeOutSamples) {
        envelope = (bufferLength - i) / fadeOutSamples;
      }

      data[i] = sample * gain * envelope;
    }

    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.masterGain);

    return source;
  }

  /**
   * Generate white noise
   */
  generateWhiteNoise(params: NoiseParams): AudioBufferSourceNode {
    const { duration, gain = 0.2, filterFrequency, filterType = 'lowpass' } = params;

    const sampleRate = this.context.sampleRate;
    const bufferLength = Math.ceil(sampleRate * duration);
    const buffer = this.context.createBuffer(1, bufferLength, sampleRate);
    const data = buffer.getChannelData(0);

    // Generate white noise
    for (let i = 0; i < bufferLength; i++) {
      data[i] = (Math.random() * 2 - 1) * gain;
    }

    const source = this.context.createBufferSource();
    source.buffer = buffer;

    if (filterFrequency) {
      const filter = this.context.createBiquadFilter();
      filter.type = filterType;
      filter.frequency.setValueAtTime(filterFrequency, this.context.currentTime);
      source.connect(filter);
      filter.connect(this.masterGain);
    } else {
      source.connect(this.masterGain);
    }

    return source;
  }

  /**
   * Generate terminal beep - simple 880Hz sine wave for 100ms
   */
  generateTerminalBeep(): AudioBufferSourceNode {
    return this.generateTone({
      type: 'sine',
      frequency: 880,
      duration: 0.1,
      gain: 0.25,
      fadeIn: 0.005,
      fadeOut: 0.02,
    });
  }

  /**
   * Generate keypress click - very short white noise burst
   */
  generateKeypress(): AudioBufferSourceNode {
    const sampleRate = this.context.sampleRate;
    const duration = 0.01; // 10ms
    const bufferLength = Math.ceil(sampleRate * duration);
    const buffer = this.context.createBuffer(1, bufferLength, sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < bufferLength; i++) {
      // Sharp attack, quick decay envelope
      const envelope = Math.exp(-i / (bufferLength * 0.2));
      data[i] = (Math.random() * 2 - 1) * 0.15 * envelope;
    }

    const source = this.context.createBufferSource();
    source.buffer = buffer;

    // Bandpass filter for more realistic click
    const filter = this.context.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(2000, this.context.currentTime);
    filter.Q.setValueAtTime(1, this.context.currentTime);

    source.connect(filter);
    filter.connect(this.masterGain);

    return source;
  }

  /**
   * Generate error buzz - descending tone 400Hz to 200Hz over 200ms
   */
  generateErrorBuzz(): AudioBufferSourceNode {
    const sampleRate = this.context.sampleRate;
    const duration = 0.2;
    const bufferLength = Math.ceil(sampleRate * duration);
    const buffer = this.context.createBuffer(1, bufferLength, sampleRate);
    const data = buffer.getChannelData(0);

    const startFreq = 400;
    const endFreq = 200;

    for (let i = 0; i < bufferLength; i++) {
      const t = i / sampleRate;
      const progress = i / bufferLength;

      // Linear frequency sweep
      const frequency = startFreq + (endFreq - startFreq) * progress;

      // Square wave for harsh sound
      const phase = 2 * Math.PI * frequency * t;
      let sample = Math.sin(phase) > 0 ? 1 : -1;

      // Add some harmonics for buzz effect
      sample += Math.sin(phase * 2) * 0.3;
      sample += Math.sin(phase * 3) * 0.1;

      // Envelope
      const envelope = 1 - progress * 0.3;

      data[i] = sample * 0.2 * envelope;
    }

    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.masterGain);

    return source;
  }

  /**
   * Generate alert klaxon - alternating 800Hz/600Hz tones
   */
  generateAlertKlaxon(cycles: number = 3): AudioBufferSourceNode {
    const sampleRate = this.context.sampleRate;
    const toneLength = 0.5; // 500ms per tone
    const duration = toneLength * 2 * cycles;
    const bufferLength = Math.ceil(sampleRate * duration);
    const buffer = this.context.createBuffer(1, bufferLength, sampleRate);
    const data = buffer.getChannelData(0);

    const highFreq = 800;
    const lowFreq = 600;
    const samplesPerTone = Math.ceil(sampleRate * toneLength);

    for (let i = 0; i < bufferLength; i++) {
      const t = i / sampleRate;
      const cyclePosition = Math.floor(i / samplesPerTone);
      const isHighTone = cyclePosition % 2 === 0;
      const frequency = isHighTone ? highFreq : lowFreq;

      // Sine wave with slight overdrive for urgency
      let sample = Math.sin(2 * Math.PI * frequency * t);
      sample = Math.tanh(sample * 1.5); // Soft clipping for edge

      // Add subtle wobble
      const wobble = Math.sin(2 * Math.PI * 5 * t) * 0.05;
      sample *= (1 + wobble);

      // Per-tone envelope for smoother transitions
      const positionInTone = i % samplesPerTone;
      const toneProgress = positionInTone / samplesPerTone;
      let envelope = 1;
      if (toneProgress < 0.05) {
        envelope = toneProgress / 0.05;
      } else if (toneProgress > 0.95) {
        envelope = (1 - toneProgress) / 0.05;
      }

      data[i] = sample * 0.35 * envelope;
    }

    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.masterGain);

    return source;
  }

  /**
   * Generate success sound - ascending arpeggio
   */
  generateSuccessSound(): AudioBufferSourceNode {
    const sampleRate = this.context.sampleRate;
    const noteLength = 0.08;
    const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
    const duration = noteLength * notes.length + 0.1;
    const bufferLength = Math.ceil(sampleRate * duration);
    const buffer = this.context.createBuffer(1, bufferLength, sampleRate);
    const data = buffer.getChannelData(0);

    const samplesPerNote = Math.ceil(sampleRate * noteLength);

    for (let i = 0; i < bufferLength; i++) {
      const t = i / sampleRate;
      let sample = 0;

      for (let n = 0; n < notes.length; n++) {
        const noteStart = n * samplesPerNote;
        const noteEnd = noteStart + samplesPerNote * 2; // Notes overlap/sustain

        if (i >= noteStart && i < noteEnd) {
          const noteProgress = (i - noteStart) / (noteEnd - noteStart);
          const envelope = Math.exp(-noteProgress * 3);
          sample += Math.sin(2 * Math.PI * notes[n] * t) * envelope * 0.2;
        }
      }

      data[i] = sample;
    }

    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.masterGain);

    return source;
  }

  /**
   * Play a generated sound immediately
   */
  playSound(source: AudioBufferSourceNode): void {
    source.start(this.context.currentTime);
  }

  /**
   * Get the audio context
   */
  getContext(): AudioContext {
    return this.context;
  }
}
